import {
  BookingLedgerRow,
  ItemSubtotalStrip,
  TemplateLedgerRow,
  VariationLedgerRow,
  canAdminCancelAllocation,
  percentOf,
  type LedgerRowMenu,
} from '@billda/design-system';
import { SITE_LEDGER_COLLAPSE, SITE_LEDGER_COLUMNS } from './columns';
import { SiteWorkHoverCard } from './SiteWorkHoverCard';
import {
  needsSubtotalStrip,
  type LedgerBooking,
  type LedgerItemEntry,
  type LedgerVariationEntry,
} from './toPlotLedgerGroups';

/** Everything a row needs from the screen around it. */
export interface SiteLedgerRowContext {
  plotId: string;
  showValues: boolean;
  /** The viewer holds the admin cancel permission. */
  canCancelBookings: boolean;
  onOpenPanel: (type: string, id: string, plotId?: string) => void;
  onAdminCancel: (allocationId: string, plotId?: string) => void;
}

const LABOUR_KIND = 'Labour item';

/**
 * Uses the same fallback rule as the details panel when this site-scoped read
 * model does not expose the current pot status.
 */
function cancelMenu(
  booking: LedgerBooking,
  ctx: SiteLedgerRowContext,
  testId: string
): LedgerRowMenu | undefined {
  if (!ctx.canCancelBookings) {
    return undefined;
  }
  return {
    label: 'Cancel Allocation',
    disabled: !canAdminCancelAllocation({
      allocationStatus: booking.status,
      hasPotSubmission: booking.hasPotSubmission,
    }),
    onSelect: () => ctx.onAdminCancel(booking.allocationId, ctx.plotId),
    'data-testid': testId,
  };
}

interface ItemLedgerRowsProps {
  entry: LedgerItemEntry;
  ctx: SiteLedgerRowContext;
}

function bookingKind(index: number, count: number): string {
  if (count <= 1) {
    return LABOUR_KIND;
  }
  return `${LABOUR_KIND} · booking ${index + 1} of ${count}`;
}

function ItemBookingRow({
  entry,
  ctx,
  booking,
  index,
}: ItemLedgerRowsProps & { booking: LedgerBooking; index: number }) {
  const count = entry.bookings.length;
  const openPanel = () =>
    ctx.onOpenPanel('allocation', booking.allocationId, ctx.plotId);

  return (
    <BookingLedgerRow
      columns={SITE_LEDGER_COLUMNS}
      collapse={SITE_LEDGER_COLLAPSE}
      title={entry.title}
      status={booking.status}
      reference={booking.reference}
      bookedBy={booking.bookedBy}
      bookedAt={booking.bookedAt}
      amount={booking.amount}
      quantity={
        booking.quantity != null && booking.unitLabel
          ? { value: booking.quantity, unitLabel: booking.unitLabel }
          : null
      }
      partLabel={count > 1 ? `${index + 1} of ${count}` : undefined}
      showValues={ctx.showValues}
      onClick={openPanel}
      menu={cancelMenu(booking, ctx, 'sitegrouped-cancel-allocation')}
      data-testid="allocation-row"
      hoverCard={
        <SiteWorkHoverCard
          title={entry.title}
          kind={bookingKind(index, count)}
          status={booking.status}
          reference={booking.reference}
          who={booking.bookedBy}
          whenIso={booking.bookedAt}
          amount={booking.amount}
          target={entry.target}
          quantity={booking.quantity}
          unitRate={booking.unitRate}
          unitLabel={booking.unitLabel}
          approvedBy={booking.approvedBy}
          approvedAt={booking.approvedAt}
          rejectedBy={booking.rejectedBy}
          rejectionReason={booking.rejectionReason}
          showValues={ctx.showValues}
          onOpenDetails={openPanel}
        />
      }
    />
  );
}

function UnbookedItemRow({ entry, ctx }: ItemLedgerRowsProps) {
  const openPanel = () =>
    ctx.onOpenPanel('labour', entry.labourItemId.toString(), ctx.plotId);

  return (
    <TemplateLedgerRow
      columns={SITE_LEDGER_COLUMNS}
      collapse={SITE_LEDGER_COLLAPSE}
      title={entry.title}
      targetAmount={entry.target}
      quantity={
        entry.pricingMode === 'UnitRate' &&
        entry.totalQuantity != null &&
        entry.unitLabel
          ? { value: entry.totalQuantity, unitLabel: entry.unitLabel }
          : null
      }
      showValues={ctx.showValues && entry.target != null}
      onClick={openPanel}
      data-testid="labour-item-row"
      hoverCard={
        <SiteWorkHoverCard
          title={entry.title}
          kind={LABOUR_KIND}
          description="Not booked yet"
          target={entry.target}
          quantity={entry.totalQuantity}
          unitRate={entry.unitRate}
          unitLabel={entry.unitLabel}
          showValues={ctx.showValues}
          onOpenDetails={openPanel}
        />
      }
    />
  );
}

/**
 * One labour item: unbooked items read as the plot's price book, booked ones
 * render a peer row per booking capped by a subtotal strip.
 */
export function ItemLedgerRows({ entry, ctx }: ItemLedgerRowsProps) {
  if (entry.isLoadingBookings) {
    return (
      <BookingLedgerRow
        columns={SITE_LEDGER_COLUMNS}
        collapse={SITE_LEDGER_COLLAPSE}
        title={entry.title}
        showValues={ctx.showValues}
        pending
      />
    );
  }

  if (entry.bookings.length === 0) {
    return <UnbookedItemRow entry={entry} ctx={ctx} />;
  }

  return (
    <div>
      {entry.bookings.map((booking, index) => (
        <ItemBookingRow
          key={booking.allocationId}
          entry={entry}
          ctx={ctx}
          booking={booking}
          index={index}
        />
      ))}
      {needsSubtotalStrip(entry) && (
        <ItemSubtotalStrip
          collapse={SITE_LEDGER_COLLAPSE}
          percentBooked={
            entry.bookedAmount == null || entry.target == null
              ? null
              : (percentOf(entry.bookedAmount, entry.target) ?? 0)
          }
          remaining={entry.remaining}
          bookedTotal={entry.bookedAmount}
          remainingQuantity={
            entry.pricingMode === 'UnitRate' &&
            entry.remainingQuantity != null &&
            entry.unitLabel
              ? {
                  value: entry.remainingQuantity,
                  unitLabel: entry.unitLabel,
                }
              : null
          }
          showValues={ctx.showValues}
        />
      )}
    </div>
  );
}

interface VariationLedgerEntryProps {
  entry: LedgerVariationEntry;
  ctx: SiteLedgerRowContext;
}

/** Day work and other variations, titled by work type rather than free text. */
export function VariationLedgerEntry({
  entry,
  ctx,
}: VariationLedgerEntryProps) {
  const openPanel = () =>
    ctx.onOpenPanel(
      entry.booking ? 'allocation' : 'variation',
      entry.booking?.allocationId ?? entry.variationId,
      ctx.plotId
    );
  const booking = entry.booking;

  if (entry.isLoadingBooking) {
    return (
      <VariationLedgerRow
        columns={SITE_LEDGER_COLUMNS}
        collapse={SITE_LEDGER_COLLAPSE}
        workTypeName={entry.workTypeName}
        status={entry.status}
        amount={entry.amount}
        showValues={ctx.showValues}
        pending
      />
    );
  }

  return (
    <VariationLedgerRow
      columns={SITE_LEDGER_COLUMNS}
      collapse={SITE_LEDGER_COLLAPSE}
      workTypeName={entry.workTypeName}
      description={entry.description}
      status={entry.status}
      reference={booking?.reference}
      bookedBy={booking?.bookedBy}
      // A variation with no allocation was still submitted by somebody at some
      // point, and that timestamp is the honest answer for "when".
      bookedAt={booking?.bookedAt ?? entry.submittedAt}
      amount={entry.amount}
      showValues={ctx.showValues}
      onClick={openPanel}
      menu={
        booking
          ? cancelMenu(booking, ctx, 'sitegrouped-cancel-variation')
          : undefined
      }
      data-testid="variation-flat-row"
      hoverCard={
        <SiteWorkHoverCard
          title={entry.description || entry.workTypeName}
          kind={entry.workTypeName}
          status={entry.status}
          reference={booking?.reference}
          who={booking?.bookedBy}
          whenIso={booking?.bookedAt ?? entry.submittedAt}
          amount={entry.amount}
          approvedBy={booking?.approvedBy}
          approvedAt={booking?.approvedAt}
          rejectedBy={booking?.rejectedBy}
          rejectionReason={booking?.rejectionReason}
          showValues={ctx.showValues}
          onOpenDetails={openPanel}
        />
      }
    />
  );
}
